import React, { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { CreditCard, Heart, Shield, ArrowRight, Calendar, DollarSign, Plus, X, CheckCircle, XCircle, Clock } from 'lucide-react'
import { useApi } from '../../contexts/ApiContext'
import { useUserId } from '../../hooks/useUserId'
import ChatButton from '../../components/ChatButton'
import { trackPageView, trackPolicyView, trackNewPolicyClick, openChatWithContext } from '../../utils/eventTracking'

const CustomerPolicies = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { getUserPolicies } = useApi()
  const { userId } = useUserId()
  
  const [policies, setPolicies] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showNewPolicyForm, setShowNewPolicyForm] = useState(false)

  // Track page view on mount
  useEffect(() => {
    trackPageView('Customer Policies', { user_id: userId })
  }, [userId])

  useEffect(() => {
    const fetchPolicies = async () => {
      if (!userId) return
      
      try {
        setLoading(true)
        const userPolicies = await getUserPolicies(userId)
        setPolicies(userPolicies || [])
      } catch (err) {
        console.error('Error fetching policies:', err)
        setError('Failed to load policies')
      } finally {
        setLoading(false)
      }
    }

    fetchPolicies()
  }, [userId, getUserPolicies])

  // Check for action parameter to show new policy form
  useEffect(() => {
    const action = searchParams.get('action')
    if (action === 'new') {
      setShowNewPolicyForm(true)
    }
  }, [searchParams])

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A'
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const healthPolicies = policies.filter(policy => policy.product_category === 'HEALTH')
  const lifePolicies = policies.filter(policy => policy.product_category === 'LIFE')

  // Get policy status icon
  const getPolicyStatusIcon = (status) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE':
        return <CheckCircle className="h-4 w-4" />
      case 'EXPIRED':
      case 'CANCELLED':
        return <XCircle className="h-4 w-4" />
      case 'PENDING':
        return <Clock className="h-4 w-4" />
      default:
        return <Shield className="h-4 w-4" />
    }
  }

  const handlePolicyClick = (policyId) => {
    console.log('Navigating to policy details:', policyId)
    navigate(`/customer/policy-details?user_id=${userId}&policy_id=${policyId}`)
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">My Policies</h1>
          <p className="text-gray-600">View and manage your insurance policies</p>
        </div>
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-500 mt-4">Loading policies...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">My Policies</h1>
          <p className="text-gray-600">View and manage your insurance policies</p>
        </div>
        <div className="text-center py-12">
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">My Policies</h1>
          <p className="text-sm sm:text-base text-gray-600">View and manage your insurance policies</p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          <ChatButton type="policy" size="small" variant="secondary" />
          <button 
            onClick={() => setShowNewPolicyForm(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center gap-2 transition-colors text-sm sm:text-base whitespace-nowrap"
          >
            <Plus className="h-4 w-4 sm:h-5 sm:w-5" />
            <span>New Policy</span>
          </button>
        </div>
      </div>

      {/* Health Insurance Section */}
      <div>
        <div className="flex items-center space-x-3 mb-6">
          <Heart className="h-6 w-6 text-red-500" />
          <h2 className="text-2xl font-bold text-gray-900">Health Insurance</h2>
          <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-medium">
            {healthPolicies.length} {healthPolicies.length === 1 ? 'Policy' : 'Policies'}
          </span>
        </div>
        
        {healthPolicies.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {healthPolicies.map((policy) => (
              <div
                key={policy.policy_id}
                onClick={() => handlePolicyClick(policy.policy_id)}
                className="bg-white border-2 border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer hover:border-red-300"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="bg-red-100 p-3 rounded-lg">
                      <Heart className="h-6 w-6 text-red-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{policy.policy_name}</h3>
                      <p className="text-sm text-gray-600">{policy.policy_number}</p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium flex items-center space-x-1 ${
                    policy.policy_status === 'ACTIVE' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {getPolicyStatusIcon(policy.policy_status)}
                    <span>{policy.policy_status}</span>
                  </span>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Sum Insured</span>
                    <span className="font-semibold text-gray-900">{formatCurrency(policy.sum_insured)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Premium</span>
                    <span className="font-semibold text-gray-900">{formatCurrency(policy.annual_premium)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Valid Until</span>
                    <span className="font-semibold text-gray-900">{formatDate(policy.policy_end_date)}</span>
                  </div>
                </div>
                
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">View Details</span>
                    <ArrowRight className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white border-2 border-gray-200 rounded-xl p-8 text-center">
            <Heart className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Health Insurance Policies</h3>
            <p className="text-gray-500">You don't have any health insurance policies yet.</p>
          </div>
        )}
      </div>

      {/* Life Insurance Section */}
      <div>
        <div className="flex items-center space-x-3 mb-6">
          <Shield className="h-6 w-6 text-blue-500" />
          <h2 className="text-2xl font-bold text-gray-900">Life Insurance</h2>
          <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
            {lifePolicies.length} {lifePolicies.length === 1 ? 'Policy' : 'Policies'}
          </span>
        </div>
        
        {lifePolicies.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {lifePolicies.map((policy) => (
              <div
                key={policy.policy_id}
                onClick={() => handlePolicyClick(policy.policy_id)}
                className="bg-white border-2 border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer hover:border-blue-300"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="bg-blue-100 p-3 rounded-lg">
                      <Shield className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{policy.policy_name}</h3>
                      <p className="text-sm text-gray-600">{policy.policy_number}</p>
                    </div>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium flex items-center space-x-1 ${
                    policy.policy_status === 'ACTIVE' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {getPolicyStatusIcon(policy.policy_status)}
                    <span>{policy.policy_status}</span>
                  </span>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Sum Insured</span>
                    <span className="font-semibold text-gray-900">{formatCurrency(policy.sum_insured)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Premium</span>
                    <span className="font-semibold text-gray-900">{formatCurrency(policy.annual_premium)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Valid Until</span>
                    <span className="font-semibold text-gray-900">{formatDate(policy.policy_end_date)}</span>
                  </div>
                </div>
                
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">View Details</span>
                    <ArrowRight className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white border-2 border-gray-200 rounded-xl p-8 text-center">
            <Shield className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Life Insurance Policies</h3>
            <p className="text-gray-500">You don't have any life insurance policies yet.</p>
          </div>
        )}
      </div>

      {/* New Policy Form Modal */}
      {showNewPolicyForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">New Policy Application</h2>
              <button 
                onClick={() => setShowNewPolicyForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="text-center py-8">
                <CreditCard className="h-12 w-12 text-blue-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Policy Application</h3>
                <p className="text-gray-600 mb-4">
                  To apply for a new insurance policy, please contact our customer service team.
                </p>
                <div className="space-y-2 text-sm text-gray-500">
                  <p>📞 Call: +91 80 1234 5678</p>
                  <p>📧 Email: insurance.devrev@gmail.com</p>
                  <p>💬 Live Chat: Available 24/7</p>
                </div>
              </div>
              
              <div className="flex space-x-3">
                <button 
                  onClick={() => {
                    setShowNewPolicyForm(false)
                    openChatWithContext(
                      'I would like to apply for a new insurance policy. Can you help me with the application process and available policy options?',
                      'new_policy_application'
                    )
                  }}
                  className="flex-1 bg-yellow-400 hover:bg-yellow-500 text-gray-900 px-4 py-2 rounded-lg font-semibold transition-colors"
                >
                  Talk to an Advisor
                </button>
                <button 
                  onClick={() => {
                    // Navigate to contact page or open live chat
                    window.open('tel:+918012345678', '_self')
                  }}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Call Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CustomerPolicies